/* External */
import {
  memo,
  Children,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import sortedIndex from 'lodash-es/sortedIndex';
import { useLocation } from 'react-router-dom';

/* Ott-common */
import Icon from '@dtvgo/icon';
import InteractiveElement from '@dtvgo/interactive-element';

/* Components */
import CardCarousel from './CardCarousel';

/* Utils */
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';
import { useIsSidebarExpanded } from '../../providers/SidebarStateContext';
import useClickCallback from '../../hooks/useClickCallback';
import useRefSync from '../../hooks/useRefSync';
import { getBEMClassName } from '../../utils/functions';
import { SELECTED_CAROUSEL_CARD_KEY } from '../../utils/constants';
import { ScrollMode } from '../ScrollView';

/* Styles */
import './Carousel.scss';

/* Measure the target card's real offset inside the content: cards are not
   uniform (filter tabs are fit-content), so the arithmetic
   cardStartPosition drifts and the focused card ends up clipped on narrow
   screens. The card-vs-content rect delta is invariant to the translateX
   transition, so the target is stable mid-animation. Returns undefined
   when the card is not mounted yet or the measurement would be wrong
   (caller falls back to arithmetic). */
const getMeasuredTranslate = ({
  containerEl,
  containerWidth,
  positionToUse,
  currentTranslate,
  centeredPosition,
  mountedPositions,
}) => {
  const cardEl = containerEl.querySelector(`[data-carousel-pos="${positionToUse}"]`);
  const contentEl = containerEl.querySelector('.dtv-html5-c-carousel__card-content');

  if (!cardEl || !contentEl) return undefined;

  /* The rect delta is only the card's true offset inside the content when
     every card before it is mounted: the windowed slice unmounts the left
     cards, so the flex layout starts the mounted subset at index 0 and the
     measured position drifts by one card per unmounted predecessor. Defer
     to the arithmetic cardStartPosition (exact for the uniform cards that
     windowing applies to) whenever the prefix is incomplete. */
  const mountedBeforeTarget = mountedPositions
    .filter((position) => position < positionToUse).length;
  if (mountedBeforeTarget !== positionToUse) return undefined;

  const cardRect = cardEl.getBoundingClientRect();
  const contentRect = contentEl.getBoundingClientRect();
  const cardLeftInContent = cardRect.left - contentRect.left;
  const cardWidthMeasured = cardRect.width;
  const contentWidth = contentEl.scrollWidth || 0;
  const maxTranslate = Math.max(0, contentWidth - containerWidth);
  const normalizeTranslate = (value) => Math.min(maxTranslate, Math.max(0, value));

  if (centeredPosition) {
    return normalizeTranslate(
      cardLeftInContent - (containerWidth - cardWidthMeasured) / 2,
    );
  }

  const cardRightInContent = cardLeftInContent + cardWidthMeasured;
  if (currentTranslate <= cardLeftInContent
    && currentTranslate + containerWidth >= cardRightInContent) {
    return currentTranslate;
  }
  if (cardRightInContent > currentTranslate + containerWidth) {
    return normalizeTranslate(cardRightInContent - containerWidth);
  }
  return normalizeTranslate(cardLeftInContent);
};

function Carousel({
  title = undefined,
  className = '',
  children: unfilteredChildren,
  focused,
  maxCardHeightRem,
  maxCardWidthRem = undefined,
  cardScrollMode = undefined,
  setFocus,
  initialTranslate = 0,
  positionCarousel,
  realFocusKey,
  withDots = false,
  hasFocusedChild,
  stealFocusByDefault = false,
  setPositionCarousel,
  centeredPosition = false,
  roundedArrows = false,
  withGradients = false,
  onTranslateChange = undefined,
  onSetFocusKey = undefined,
  isModalShown = false,
}) {
  const uniqueCardFocusKey = `${realFocusKey}_CARD_`;
  const container = useRef();
  const { pathname } = useLocation();
  const detailPageUrlRegex = useMemo(() => /.*\/detail\/.*/, []);
  const isNotDetailPage = !detailPageUrlRegex.test(pathname);

  const children = useMemo(
    () => Children.toArray(unfilteredChildren).filter((child) => !!child),
    [unfilteredChildren],
  );
  const numberOfChildren = useMemo(() => Children.count(children), [children]);
  const numberOfChildrenRef = useRefSync(numberOfChildren);

  const [translate, setTranslate] = useState(Math.abs(initialTranslate) || 0);

  /* Uniform card width (px), measured from the first mounted card and kept
     fresh by a ResizeObserver. Cards are uniform per carousel, so the whole
     navigation math (cardStartPosition, windowing) is arithmetic. */
  const [cardWidth, setCardWidth] = useState(undefined);

  const isSidebarExpanded = useIsSidebarExpanded();
  const isSidebarExpandedRef = useRefSync(isSidebarExpanded);

  const cardStartPosition = useMemo(() => {
    const accumulatedCardWidths = [0];
    const width = cardWidth || 0;
    for (let index = 1; index <= numberOfChildren; index += 1) {
      /* +1px inter-card margin (Carousel.scss `> * + *`) so the cumulative
         start of each card matches the rect-delta positions
         getMeasuredTranslate measures. */
      accumulatedCardWidths.push(accumulatedCardWidths[index - 1] + width + 1);
    }

    return accumulatedCardWidths;
  }, [cardWidth, numberOfChildren]);
  const cardStartPositionRef = useRefSync(cardStartPosition);

  const normalizePosition = useCallback(
    (position) => Math.min(numberOfChildrenRef.current - 1, Math.max(0, position)),
    [numberOfChildrenRef],
  );

  /* Windowed slice of the mounted cards: only the cards around the current
     scroll position plus the focused one are rendered, instead of mounting
     every child on page load. Focus keys and order are unchanged. */
  const windowedIndexes = useMemo(() => {
    const width = cardWidth || 0;
    if (!width || numberOfChildren === 0) {
      // Nothing measured yet: mount the first card so its width is reported.
      return [0];
    }
    const viewportWidth = container.current?.clientWidth || 0;
    const firstIndex = Math.max(0, Math.floor(translate / width) - 2);
    const lastIndex = Math.min(
      numberOfChildren - 1,
      Math.ceil((translate + viewportWidth) / width) + 2,
    );
    const indexes = new Set();
    for (let index = firstIndex; index <= lastIndex; index += 1) {
      indexes.add(index);
    }
    // Spatial focus must always land on a mounted card.
    indexes.add(normalizePosition(positionCarousel));
    return [...indexes].sort((a, b) => a - b);
  }, [cardWidth, numberOfChildren, normalizePosition, positionCarousel, translate]);

  const windowedIndexesRef = useRefSync(windowedIndexes);

  const { firstVisible, lastVisible } = useMemo(() => ({
    firstVisible: normalizePosition(sortedIndex(cardStartPosition, translate)),
    lastVisible: normalizePosition(
      sortedIndex(
        cardStartPosition,
        translate + (container.current?.clientWidth || 0) + 1 || 0,
      ) - 2,
    ),
  }), [cardStartPosition, normalizePosition, translate]);

  const firstVisibleRef = useRefSync(firstVisible);
  const lastVisibleRef = useRefSync(lastVisible);

  const handleWidthChange = useCallback((width) => {
    setCardWidth(width);
  }, []);

  /* Re-measure the uniform card width when the container resizes (zoom,
     sidebar). Missing ResizeObserver on very old devices: fall back to the
     onWidthChange reports from the mounted cards. */
  useEffect(() => {
    const containerEl = container.current;
    if (!containerEl || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const firstCard = containerEl.querySelector('.dtv-html5-c-carousel__card');
      if (firstCard) {
        setCardWidth(firstCard.clientWidth);
      }
    });
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, []);

  const showArrowLeft = firstVisible > 0;
  const showArrowRight = lastVisible < numberOfChildren - 1;

  const goToPosition = useCallback((position) => {
    const positionToUse = normalizePosition(position);
    setPositionCarousel(positionToUse);
    setTranslate((currentTranslate) => {
      const containerEl = container.current;
      if (!containerEl) return currentTranslate;
      const containerWidth = containerEl.clientWidth || 0;

      const measuredTranslate = getMeasuredTranslate({
        containerEl,
        containerWidth,
        positionToUse,
        currentTranslate,
        centeredPosition,
        mountedPositions: windowedIndexesRef.current,
      });
      if (measuredTranslate !== undefined) return measuredTranslate;

      /* Fallback (target card not mounted yet): arithmetic card positions. */
      const startPosition = cardStartPositionRef.current[positionToUse];
      const endPosition = cardStartPositionRef.current[positionToUse + 1];
      if ((!startPosition && startPosition !== 0) || (!endPosition && endPosition !== 0)) {
        return currentTranslate;
      }

      const isFullyVisible = currentTranslate <= startPosition
        && currentTranslate + containerWidth >= endPosition;
      const maxTranslateFallback = Math.max(
        0,
        cardStartPositionRef.current[cardStartPositionRef.current.length - 1] - containerWidth,
      );
      const normalizeTranslateFallback = (value) => (
        Math.min(maxTranslateFallback, Math.max(0, value))
      );

      if (centeredPosition) {
        return normalizeTranslateFallback(
          (startPosition + (endPosition - startPosition) / 2) - containerWidth / 2,
        );
      }

      if (isFullyVisible) return currentTranslate;

      if (endPosition > currentTranslate + containerWidth) {
        return normalizeTranslateFallback(endPosition - containerWidth);
      }

      return normalizeTranslateFallback(startPosition);
    });
  }, [
    cardStartPositionRef,
    centeredPosition,
    normalizePosition,
    setPositionCarousel,
    windowedIndexesRef,
  ]);

  const prevCard = useClickCallback(useCallback(
    () => {
      goToPosition(firstVisibleRef.current - 1);
    },
    [goToPosition, firstVisibleRef],
  ));

  const nextCard = useClickCallback(useCallback(
    () => {
      goToPosition(lastVisibleRef.current + 1);
    },
    [lastVisibleRef, goToPosition],
  ));

  const dots = useMemo(() => (
    withDots
      ? (Array.from({ length: numberOfChildren }))?.map((undef, index) => (
        <div
          /* eslint-disable-next-line react/no-array-index-key
              -- it's okay since it's only presentational */
          key={index}
          className={getBEMClassName('carousel', 'dot', { active: positionCarousel === index })}
        />
      )) || undefined
      : undefined
  ), [withDots, numberOfChildren, positionCarousel]);

  const cards = useMemo(() => windowedIndexes.map((position) => {
    const card = children[position];
    if (!card) {
      return undefined;
    }
    return (
      <CardCarousel
        key={card.key ?? position}
        width={maxCardWidthRem}
        focusKey={`${uniqueCardFocusKey}${position}`}
        onWidthChange={handleWidthChange}
        mode={cardScrollMode}
        cardPosition={position}
      >
        {card}
      </CardCarousel>
    );
  }), [
    children,
    maxCardWidthRem,
    uniqueCardFocusKey,
    cardScrollMode,
    windowedIndexes,
    handleWidthChange,
  ]);

  /* Reserve the leading (unmounted) cards' space so the windowed slice does
     not re-flow: without this, unmounting cards 0..k pulls every mounted
     card k card-widths left while translateX keeps its full offset, so the
     focused card drifts one card off-screen per step past the window edge.
     cardStartPosition[firstMounted] is exactly the freed width (measured per
     position, 1px inter-card margin included). */
  const firstMountedIndex = windowedIndexes[0] ?? 0;
  const leadingReservedWidth = cardStartPosition[firstMountedIndex] || 0;
  const cardContentStyle = useMemo(() => ({
    transform: `translateX(-${translate}px)`,
    height: `${maxCardHeightRem}rem`,
    paddingLeft: leadingReservedWidth ? `${leadingReservedWidth}px` : undefined,
    willChange: 'transform',
  }), [translate, maxCardHeightRem, leadingReservedWidth]);

  useEffect(() => {
    onTranslateChange?.(translate);
  }, [onTranslateChange, translate]);

  useEffect(() => {
    onSetFocusKey?.(`${uniqueCardFocusKey}${positionCarousel}`);
  }, [onSetFocusKey, uniqueCardFocusKey, positionCarousel]);

  useEffect(() => {
    goToPosition(positionCarousel);
  }, [goToPosition, positionCarousel, cardStartPosition]);

  useEffect(() => {
    const { positionCarouselStored, titleCarouselStored } = JSON.parse(sessionStorage.getItem(
      SELECTED_CAROUSEL_CARD_KEY,
    )) || {};

    if (title === titleCarouselStored && !!positionCarouselStored) {
      goToPosition(positionCarouselStored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- To only run on mount
  }, []);

  useEffect(() => {
    if (stealFocusByDefault && !positionCarousel) {
      if (!isSidebarExpandedRef.current && !isModalShown) {
        setFocus();
      } else {
        sessionStorage.setItem('focusableItemInMain', `${uniqueCardFocusKey}0`);
      }
    }
  }, [
    isSidebarExpandedRef,
    isModalShown,
    positionCarousel,
    setFocus,
    stealFocusByDefault,
    uniqueCardFocusKey,
  ]);

  useEffect(() => {
    if (isNotDetailPage && (focused || hasFocusedChild)) {
      if (positionCarousel > 0) {
        setFocus(`${uniqueCardFocusKey}${positionCarousel}`);
        sessionStorage.setItem(
          SELECTED_CAROUSEL_CARD_KEY,
          JSON.stringify({
            positionCarouselStored: positionCarousel,
            titleCarouselStored: title,
          }),
        );
      } else {
        setFocus(`${uniqueCardFocusKey}0`);
        sessionStorage.setItem(
          SELECTED_CAROUSEL_CARD_KEY,
          JSON.stringify({
            positionCarouselStored: 0,
            titleCarouselStored: title,
          }),
        );
      }
    }
  }, [
    uniqueCardFocusKey,
    focused,
    setFocus,
    positionCarousel,
    hasFocusedChild,
    title,
    isNotDetailPage,
  ]);

  return (
    <div className={`${getBEMClassName('carousel', undefined, { 'with-gradients': withGradients })} ${className}`}>
      {(title) && (
        <h3 className={getBEMClassName('carousel', 'title')}>
          {title}
        </h3>
      )}
      <div className={getBEMClassName('carousel', 'container')}>
        <div className={getBEMClassName('carousel', 'arrow-left-container', { visible: showArrowLeft })}>
          <InteractiveElement
            className={getBEMClassName('carousel', 'arrow-left', { rounded: roundedArrows })}
            onMouseDown={prevCard}
          >
            <Icon
              name="arrow-forward"
              className={getBEMClassName('carousel', 'arrow-left-icon')}
            />
          </InteractiveElement>
        </div>
        <div className={getBEMClassName('carousel', 'card-content-container')} ref={container}>
          <div className={getBEMClassName('carousel', 'card-content')} style={cardContentStyle}>
            {cards}
          </div>
        </div>
        <div className={getBEMClassName('carousel', 'arrow-right-container', { visible: showArrowRight })}>
          <InteractiveElement
            className={getBEMClassName('carousel', 'arrow-right', { rounded: roundedArrows })}
            onMouseDown={nextCard}
          >
            <Icon
              name="arrow-forward"
              className={getBEMClassName('carousel', 'arrow-right-icon')}
            />
          </InteractiveElement>
        </div>
      </div>
      {withDots && (
        <div className={getBEMClassName('carousel', 'dots')}>
          {dots}
        </div>
      )}
    </div>
  );
}

Carousel.propTypes = {
  maxCardHeightRem: PropTypes.number.isRequired,
  maxCardWidthRem: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  focused: PropTypes.bool.isRequired,
  setFocus: PropTypes.func.isRequired,
  positionCarousel: PropTypes.number.isRequired,
  realFocusKey: PropTypes.string.isRequired,
  hasFocusedChild: PropTypes.bool.isRequired,
  cardScrollMode: PropTypes.oneOf([
    ScrollMode.ALWAYS,
    ScrollMode.IF_NEEDED,
  ]),
  withDots: PropTypes.bool,
  title: PropTypes.string,
  stealFocusByDefault: PropTypes.bool,
  setPositionCarousel: PropTypes.func.isRequired,
  centeredPosition: PropTypes.bool,
  roundedArrows: PropTypes.bool,
  withGradients: PropTypes.bool,
  initialTranslate: PropTypes.number,
  onTranslateChange: PropTypes.func,
  onSetFocusKey: PropTypes.func,
  isModalShown: PropTypes.bool,
};

export default memo(withCursorVisibilityFilterFocusable({ trackChildren: true })(memo(Carousel)));
